import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';
import { PROJECT_CONTEXT_TOKEN } from '@cosmos/angular/utils';
import { ControlLotStatus } from '@cosmos/shared';
import { environment } from '../../../../../../environments/environment';

export interface ControlLot {
  _id: string;
  code: string;
  project: string;
  concrete: any;
  structuralElementType: string;
  elementSetId: string;
  status: ControlLotStatus;
  startDate: Date;
  closeDate?: Date;
  currentVolume: number;
  pours: string[];
  testResults: string[];
  acceptanceData?: any;
}

@Injectable({
  providedIn: 'root'
})
export class LotsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.API_URL}/lots`;
  private readonly legacyApiUrl = `${environment.API_URL}/quality-control`;

  // Signals for reactive state
  readonly lots = signal<ControlLot[]>([]);
  readonly loading = signal(false);

  find(query = ''): Observable<HttpResponse<any>> {
    return this.http.get<any>(`${this.apiUrl}${query}`, { observe: 'response' }).pipe(
      map(response => response)
    );
  }

  /**
   * Get all lots with optional filters
   */
  findAll(params?: {
    project?: string;
    concrete?: string;
    elementSetId?: string;
    status?: string;
    code?: string;
    populate_concrete?: string;
    populate_project?: string;
    populate_pours?: string;
    populate_testResults?: string;
    limit?: number;
    skip?: number;
    sort?: string;
    select?: string;
  }): Observable<ControlLot[]> {
    this.loading.set(true);
    
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        const value = (params as any)[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }

    return this.http.get<ControlLot[]>(this.apiUrl, { params: httpParams }).pipe(
      tap(lots => {
        this.lots.set(lots);
        this.loading.set(false);
      })
    );
  }

  /**
   * Returns the total count of control lots matching the given filter params.
   * Calls the same endpoint as findAll with `count=true` appended.
   */
  count(params?: {
    project?: string;
    concrete?: string;
    elementSetId?: string;
    status?: string;
    code?: string;
  }): Observable<number> {
    let httpParams = new HttpParams().set('count', 'true');
    if (params) {
      Object.keys(params).forEach(key => {
        const value = (params as any)[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }
    return this.http.get<number>(this.apiUrl, { params: httpParams });
  }

  /**
   * Get lots for a specific project
   */
  findByProject(projectId: string): Observable<ControlLot[]> {
    return this.findAll({ project: projectId });
  }

  /**
   * Get a specific lot by ID
   */
  findById(lotId: string, params?: {
    populate_concrete?: string;
    populate_project?: string;
    populate_pours?: string;
    populate_testResults?: string;
    select?: string;
  }): Observable<ControlLot> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        const value = (params as any)[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }

    return this.http.get<ControlLot>(`${this.apiUrl}/${lotId}`, { params: httpParams });
  }

  /**
   * Create a new lot
   */
  create(lotData: Partial<ControlLot>, projectId?: string): Observable<ControlLot> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.post<ControlLot>(this.apiUrl, lotData, { context });
  }

  /**
   * Update a lot
   */
  update(lotId: string, updateData: Partial<ControlLot>, projectId?: string): Observable<ControlLot> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.patch<ControlLot>(`${this.apiUrl}/${lotId}`, updateData, { context });
  }

  /**
   * Delete a lot
   */
  delete(lotId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${lotId}`);
  }

  closeLot(lotId: string, projectId?: string): Observable<ControlLot> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.post<ControlLot>(`${this.apiUrl}/${lotId}/close`, {}, { context });
  }

  /** CLOSED → OPEN */
  reopenLot(lotId: string, projectId?: string): Observable<any> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.post(`${this.apiUrl}/${lotId}/reopen`, {}, { context });
  }

  /** ACCEPTED/REJECTED → CLOSED */
  rollbackAcceptance(lotId: string, projectId?: string): Observable<any> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.post(`${this.apiUrl}/${lotId}/rollback-acceptance`, {}, { context });
  }

  /**
   * Get project lots
   */
  getProjectLots(projectId: string): Observable<ControlLot[]> {
    this.loading.set(true);
    return this.http.get<ControlLot[]>(`${this.apiUrl}?project=${projectId}`).pipe(
      tap(lots => {
        this.lots.set(lots);
        this.loading.set(false);
      })
    );
  }

  /**
   * Get detailed lot information
   */
  getLotDetails(lotId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/${lotId}/details`);
  }

  /**
   * Unlink a pour from a lot
   */
  unlinkPour(lotId: string, pourId: string, projectId?: string): Observable<any> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.delete(`${this.apiUrl}/${lotId}/pours/${pourId}`, { context });
  }

  /**
   * Unlink a test result from a lot
   */
  unlinkResult(lotId: string, resultId: string, projectId?: string): Observable<any> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.delete(`${this.apiUrl}/${lotId}/results/${resultId}`, { context });
  }

  /**
   * Calculate lot acceptance
   */
  calculateLotAcceptance(lotId: string, projectId?: string): Observable<any> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.post(`${this.apiUrl}/${lotId}/calculate-acceptance`, {}, { context });
  }

  getUnlinkedResults(
    projectId: string,
    concreteId?: string,
    date?: Date,
    withRecommendations?: boolean,
  ): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/results/unlinked/${projectId}`, {
      params: {
        ...(concreteId && { concreteId }),
        ...(date && { date: date.toISOString() }),
        ...(withRecommendations && { recommendations: 'true' }),
      }
    });
  }

  /**
   * Link a test result to a lot (legacy endpoint)
   */
  linkResultToLot(resultId: string, createNewLot: boolean, existingLotId?: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/results/${resultId}/link`, {
      createNewLot,
      existingLotId
    });
  }

  /**
   * Include an STR in a lot (manual action from lot page — moves from Excluded to In Lot list).
   */
  includeStrInLot(lotId: string, strId: string, projectId?: string): Observable<any> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.post(`${this.apiUrl}/${lotId}/results/${strId}/include`, {}, { context });
  }

  /**
   * Exclude an STR from a lot (manual action from lot page — moves from In Lot to Excluded list).
   */
  excludeStrFromLot(lotId: string, strId: string, projectId?: string): Observable<any> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.delete(`${this.apiUrl}/${lotId}/results/${strId}/exclude`, { context });
  }

  /**
   * Admin: Reset QC batching for a project.
   * Deletes all control lots, resets pour linked status, resets test-result linked status.
   */
  resetQcBatching(projectId: string): Observable<{ deletedLots: number }> {
    return this.http.post<{ deletedLots: number }>(`${this.apiUrl}/admin/reset-project/${projectId}`, {});
  }
}
